import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dumbbell, Calendar, Clock, Users, MapPin, Search, Star, X, SlidersHorizontal, Grid, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import ClassCalendar from '../components/ClassCalendar';

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [calendarClasses, setCalendarClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  // View mode: 'grid' or 'calendar'
  const [viewMode, setViewMode] = useState('grid');
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await apiClient.get('/classes');
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to load classes:', error.message);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendarClasses = useCallback(async () => {
    try {
      const response = await apiClient.get('/classes/calendar');
      setCalendarClasses(response.data);
    } catch (error) {
      console.error('Failed to load calendar classes:', error.message);
    }
  }, []);

  useEffect(() => {
    fetchClasses();
    fetchCalendarClasses();
  }, [fetchClasses, fetchCalendarClasses]);

  // Get unique locations and instructors for filters
  const locations = useMemo(() => {
    const locs = [...new Set(classes.map(c => c.location || 'Main Studio'))];
    return locs.sort();
  }, [classes]);

  const instructors = useMemo(() => {
    const instr = [...new Set(classes.map(c => c.instructor))];
    return instr.sort();
  }, [classes]);

  // Filtered classes
  const filteredClasses = useMemo(() => {
    return classes.filter(cls => {
      // Search query (title, description, instructor)
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        cls.title.toLowerCase().includes(searchLower) ||
        cls.description.toLowerCase().includes(searchLower) ||
        cls.instructor.toLowerCase().includes(searchLower) ||
        (cls.location || '').toLowerCase().includes(searchLower);
      
      // Location filter
      const matchesLocation = !selectedLocation || (cls.location || 'Main Studio') === selectedLocation;
      
      // Instructor filter
      const matchesInstructor = !selectedInstructor || cls.instructor === selectedInstructor;
      
      // Price filter
      const matchesPrice = cls.price >= priceRange[0] && cls.price <= priceRange[1];
      
      return matchesSearch && matchesLocation && matchesInstructor && matchesPrice;
    });
  }, [classes, searchQuery, selectedLocation, selectedInstructor, priceRange]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedLocation('');
    setSelectedInstructor('');
    setPriceRange([0, 100]);
  };

  const hasActiveFilters = searchQuery || selectedLocation || selectedInstructor || priceRange[0] > 0 || priceRange[1] < 100;

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} className="w-4 h-4 fill-amber-400/50 text-amber-400" />);
      } else {
        stars.push(<Star key={i} className="w-4 h-4 text-neutral-600" />);
      }
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Dumbbell className="w-8 h-8 text-primary" />
            <span className="font-heading text-2xl font-bold tracking-tight">T-Fit Inc Classes</span>
          </Link>
          <div className="flex gap-4">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button data-testid="nav-dashboard-btn">Dashboard</Button>
                </Link>
              </>
            ) : (
              <Link to="/login">
                <Button data-testid="nav-login-btn">Login</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <section className="pt-32 pb-8 bg-background-paper">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-5xl sm:text-6xl font-black mb-4">
              BROWSE <span className="text-primary">CLASSES</span>
            </h1>
            <p className="text-lg text-neutral-300 max-w-2xl">
              Find the perfect class to match your fitness goals. Book your spot and pay securely.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-6 bg-background-paper border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
              <Input
                type="text"
                placeholder="Search classes, instructors, locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background border-white/10"
                data-testid="search-input"
              />
            </div>
            
            {/* Filter Toggle Button */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-white/10 ${showFilters ? 'bg-primary/20 border-primary' : ''}`}
              data-testid="filter-toggle-btn"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 rounded-full bg-primary" />
              )}
            </Button>
            
            {/* View Toggle */}
            <div className="flex border border-white/10 rounded-md overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                onClick={() => setViewMode('grid')}
                className={`rounded-none ${viewMode === 'grid' ? 'bg-primary' : ''}`}
                data-testid="view-grid-btn"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                onClick={() => setViewMode('calendar')}
                className={`rounded-none ${viewMode === 'calendar' ? 'bg-primary' : ''}`}
                data-testid="view-calendar-btn"
              >
                <CalendarDays className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-neutral-400 hover:text-white"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-background rounded-sm border border-white/10"
            >
              <div className="grid md:grid-cols-3 gap-4">
                {/* Location Filter */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500 mb-2 block">Location</label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full bg-background-paper border border-white/10 rounded-sm p-2 text-white"
                    data-testid="location-filter"
                  >
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                {/* Instructor Filter */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500 mb-2 block">Instructor</label>
                  <select
                    value={selectedInstructor}
                    onChange={(e) => setSelectedInstructor(e.target.value)}
                    className="w-full bg-background-paper border border-white/10 rounded-sm p-2 text-white"
                    data-testid="instructor-filter"
                  >
                    <option value="">All Instructors</option>
                    {instructors.map(instr => (
                      <option key={instr} value={instr}>{instr}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500 mb-2 block">
                    Max Price: £{priceRange[1]}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full accent-primary"
                    data-testid="price-filter"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Results Count */}
          <div className="mt-4 text-sm text-neutral-400">
            Showing {filteredClasses.length} of {classes.length} classes
          </div>
        </div>
      </section>

      {/* Classes Grid / Calendar */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {viewMode === 'calendar' ? (
            <ClassCalendar classes={calendarClasses} view="week" />
          ) : filteredClasses.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-400 text-lg">No classes match your filters.</p>
              <p className="text-neutral-500 text-sm mt-2">Try adjusting your search criteria.</p>
              <Button onClick={clearFilters} className="mt-4" data-testid="clear-filters-empty">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClasses.map((cls, index) => (
                <motion.div
                  key={cls.class_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`class-card-${index}`}
                >
                  <Link to={`/classes/${cls.class_id}`} className="block">
                    <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-sm overflow-hidden hover:border-primary/50 transition-all duration-300 group">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={cls.image_url}
                          alt={cls.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="font-heading text-2xl font-bold mb-1">{cls.title}</h3>
                          <p className="text-sm text-neutral-300">{cls.instructor}</p>
                        </div>
                        {/* Rating Badge */}
                        {cls.review_count > 0 && (
                          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-sm flex items-center gap-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            <span className="text-sm font-bold">{cls.average_rating?.toFixed(1) || '0.0'}</span>
                            <span className="text-xs text-neutral-400">({cls.review_count})</span>
                          </div>
                        )}
                      </div>

                      <div className="p-6 space-y-4">
                        <p className="text-neutral-400 text-sm line-clamp-2">{cls.description}</p>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-neutral-300">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span>{format(new Date(cls.class_datetime), 'MMM dd')}</span>
                          </div>
                          <div className="flex items-center gap-2 text-neutral-300">
                            <Clock className="w-4 h-4 text-primary" />
                            <span>{cls.duration} min</span>
                          </div>
                          <div className="flex items-center gap-2 text-neutral-300">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span className="truncate">{cls.location || 'Main Studio'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-neutral-300">
                            <Users className="w-4 h-4 text-primary" />
                            <span>{cls.spots_available}/{cls.capacity}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-white/10">
                          <span className="text-2xl font-bold text-primary">£{cls.price}</span>
                          <Button
                            className="bg-primary hover:bg-primary-hover font-bold"
                            data-testid={`book-class-btn-${index}`}
                          >
                            VIEW DETAILS
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Classes;
